import moment from "moment-timezone";
import * as React from "react";
import { useEffect, useState } from "react";
import { Badge, Col, Nav, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";
import { objectToArray, setPageTitle } from "../../../functions";
import { useGetDbData } from "../../../hooks/getDbData";
import { GameGenreI, GameI, PlatformI } from "../../../types";
import GamePreview from "./GamePreview";
import GameSearch from "./GameSearch";
import Ranking from "./Ranking";
import { Helmet } from "react-helmet";
import { siteTitle } from "../../../settings";

export const Games: React.FC = () => {
    const { t } = useTranslation();
    const { platform, orderBy, orderCharacter } = useParams();
    console.log("platform: ", platform, ", orderBy: ", orderBy, " orderCharacter: ", orderCharacter);

    const [games, setGames] = useState<GameI[]>();
    const [filteredGames, setFilteredGames] = useState<GameI[]>();
    const [topTwentyGames, setTopTwentyGames] = useState<GameI[]>();
    const [worstTwentyGames, setWorstTwentyGames] = useState<GameI[]>();
    const [newTwentyGames, setNewTwentyGames] = useState<GameI[]>();
    const [genres, setGenres] = useState<GameGenreI[]>();
    const [platforms, setPlatforms] = useState<PlatformI[]>();
    const [realPlatform, setRealPlatform] = useState<string>();
    const [platformGames, setPlatformGames] = useState<GameI[]>();

    const { sendRequest: fetchDbData } = useGetDbData();

    const alphabet = [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
    ];

    const charHasGames = (char: string): boolean => {
        if (platformGames) {
            return (
                [...platformGames].filter((game: GameI) => {
                    return game.title[0].toLowerCase() === char.toLowerCase();
                }).length > 0
            );
        } else if (games) {
            return (
                [...games].filter((game: GameI) => {
                    return game.title[0].toLowerCase() === char.toLowerCase();
                }).length > 0
            );
        } else {
            return false;
        }
    };

    const genreHasGames = (genre: string): boolean => {
        if (platformGames) {
            return (
                [...platformGames].filter((game: GameI) => {
                    return game.genre ? game.genre.includes(genre) : false;
                }).length > 0
            );
        } else if (games) {
            return (
                [...games].filter((game: GameI) => {
                    return game.genre ? game.genre.includes(genre) : false;
                }).length > 0
            );
        } else {
            return false;
        }
    };

    const platformHasGames = (platform: string): boolean => {
        if (games) {
            return (
                [...games].filter((game: GameI) => {
                    return game.platform === platform;
                }).length > 0
            );
        } else {
            return false;
        }
    };

    useEffect(() => {
        setPageTitle(t("Games"));

        fetchDbData<GameI>({ table: "game" }, (dbData) => {
            setGames(objectToArray<GameI>(dbData));
        });

        fetchDbData<GameGenreI>({ table: "genre" }, (dbData) => {
            setGenres(objectToArray<GameGenreI>(dbData));
        });

        fetchDbData<PlatformI>({ table: "platform" }, (dbData) => {
            setPlatforms(objectToArray<PlatformI>(dbData));
        });

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (orderBy && orderCharacter) {
            if (orderBy === "genre") {
                if (platformGames) {
                    setFilteredGames(
                        [...platformGames]
                            .filter((game: GameI) => {
                                return game.genre ? game.genre.includes(orderCharacter) : false;
                            })
                            .sort((gameA: GameI, gameB: GameI) => {
                                const a = gameA.title.toUpperCase();
                                const b = gameB.title.toUpperCase();

                                if (a < b) {
                                    return -1;
                                }

                                if (a > b) {
                                    return 1;
                                }

                                return 0;
                            })
                    );
                    window.scrollTo(0, 0);
                } else if (games) {
                    setFilteredGames(
                        [...games]
                            .filter((game: GameI) => {
                                return game.genre ? game.genre.includes(orderCharacter) : false;
                            })
                            .sort((gameA: GameI, gameB: GameI) => {
                                const a = gameA.title.toUpperCase();
                                const b = gameB.title.toUpperCase();

                                if (a < b) {
                                    return -1;
                                }

                                if (a > b) {
                                    return 1;
                                }

                                return 0;
                            })
                    );
                    window.scrollTo(0, 0);
                }
            } else {
                fetchDbData<GameI>(
                    {
                        table: "game",
                        orderBy: orderBy,
                        where: orderCharacter?.toUpperCase(),
                        isExact: true,
                    },
                    (dbData) => {
                        if (platform) {
                            setFilteredGames(
                                [...objectToArray<GameI>(dbData)]
                                    .filter((game: GameI) => {
                                        return game.platform === realPlatform;
                                    })
                                    .sort((gameA: GameI, gameB: GameI) => {
                                        const a = gameA.title.toUpperCase();
                                        const b = gameB.title.toUpperCase();

                                        if (a < b) {
                                            return -1;
                                        }

                                        if (a > b) {
                                            return 1;
                                        }

                                        return 0;
                                    })
                            );
                        } else {
                            setFilteredGames(
                                [...objectToArray<GameI>(dbData)].sort((gameA: GameI, gameB: GameI) => {
                                    const a = gameA.title.toUpperCase();
                                    const b = gameB.title.toUpperCase();

                                    if (a < b) {
                                        return -1;
                                    }

                                    if (a > b) {
                                        return 1;
                                    }

                                    return 0;
                                })
                            );
                        }
                    }
                );
            }
        } else {
            fetchDbData<GameI>(
                {
                    table: "game",
                    orderBy: orderBy,
                    where: orderCharacter?.toUpperCase(),
                    isExact: true,
                },
                (dbData) => {
                    if (platform) {
                        setFilteredGames(
                            [...objectToArray<GameI>(dbData)]
                                .filter((game: GameI) => {
                                    return game.platform === realPlatform;
                                })
                                .sort((gameA: GameI, gameB: GameI) => {
                                    const a = gameA.title.toUpperCase();
                                    const b = gameB.title.toUpperCase();

                                    if (a < b) {
                                        return -1;
                                    }

                                    if (a > b) {
                                        return 1;
                                    }

                                    return 0;
                                })
                        );
                    } else {
                        setFilteredGames([]);
                    }
                }
            );
        }
        // eslint-disable-next-line
    }, [orderBy, orderCharacter, games, platformGames]);

    useEffect(() => {
        if (platformGames) {
            const topTwenty = [...platformGames].sort((gameA: GameI, gameB: GameI) => {
                return gameB.rating.total - gameA.rating.total;
            });
            setTopTwentyGames(topTwenty.splice(0, 20));

            const worstTwenty = [...platformGames].filter((game: GameI) => {
                return game.rating.total > 0;
            });
            worstTwenty.sort((gameA: GameI, gameB: GameI) => {
                return gameA.rating.total - gameB.rating.total;
            });
            setWorstTwentyGames(worstTwenty.splice(0, 20));

            const newTwenty = [...platformGames].sort((gameA: GameI, gameB: GameI) => {
                return Number(moment(gameB.date.added).format("X")) - Number(moment(gameA.date.added).format("X"));
            });
            setNewTwentyGames(newTwenty.splice(0, 20));
        } else if (games) {
            const topTwenty = [...games].sort((gameA: GameI, gameB: GameI) => {
                return gameB.rating.total - gameA.rating.total;
            });
            setTopTwentyGames(topTwenty.splice(0, 20));

            const worstTwenty = [...games].filter((game: GameI) => {
                return game.rating.total > 0;
            });
            worstTwenty.sort((gameA: GameI, gameB: GameI) => {
                return gameA.rating.total - gameB.rating.total;
            });
            setWorstTwentyGames(worstTwenty.splice(0, 20));

            const newTwenty = [...games].sort((gameA: GameI, gameB: GameI) => {
                return Number(moment(gameB.date.added).format("X")) - Number(moment(gameA.date.added).format("X"));
            });
            setNewTwentyGames(newTwenty.splice(0, 20));
        }
    }, [games, platformGames]);

    useEffect(() => {
        if (platform && platforms) {
            setRealPlatform(
                [...platforms].filter((p: PlatformI) => {
                    return p.url === platform;
                })[0].title
            );
        } else {
            setRealPlatform("");
        }
    }, [platform, platforms]);

    useEffect(() => {
        if (realPlatform && games) {
            setPlatformGames(
                [...games].filter((game: GameI) => {
                    return game.platform === realPlatform;
                })
            );

            setFilteredGames([]);
        } else {
            setPlatformGames(games);
            setFilteredGames([]);
        }
    }, [realPlatform, games]);

    return (
        <>
            <Helmet>
                <title>
                    {t("Games")} | {siteTitle}
                </title>
            </Helmet>

            <Row className="mb-3">
                <Col xs={12} md={6}>
                    <h1>{t("Games")}</h1>
                </Col>
                <Col xs={12} md={6}>
                    {games ? <GameSearch games={platformGames ?? games} /> : null}
                </Col>
            </Row>

            {games ? (
                <>
                    <Nav className={"mb-3"}>
                        {alphabet.map((char) => (
                            <Nav.Item key={`${char.toLowerCase()}`} className={"mx-1 my-1"}>
                                <NavLink
                                    className={`btn btn-sm ${
                                        charHasGames(char) ? "btn-primary" : "btn-outline-primary disabled"
                                    }`}
                                    to={`/games${platform ? "/" + platform : ""}${
                                        orderCharacter !== char.toLocaleLowerCase()
                                            ? "/title/" + char.toLowerCase()
                                            : ""
                                    }`}
                                >
                                    {char}
                                </NavLink>
                            </Nav.Item>
                        ))}
                    </Nav>
                </>
            ) : null}

            <Row>
                {games && platforms ? (
                    <Col xs={12} md={6}>
                        <h3>{t("Platform")}:</h3>
                        <Nav className={"mb-3"}>
                            {platforms.map((platf: PlatformI) => (
                                <Nav.Item key={`${platf.url}`} className={"mx-1 my-1"}>
                                    <NavLink
                                        className={`btn btn-sm ${
                                            platformHasGames(platf.title)
                                                ? "btn-primary"
                                                : "btn-outline-primary disabled"
                                        }`}
                                        to={`/games${platform !== platf.url ? "/" + platf.url : ""}${
                                            orderBy ? `/${orderBy}/${orderCharacter}` : ""
                                        }`}
                                    >
                                        {platf.title}{" "}
                                        <Badge bg={"info"}>
                                            {[...games].filter((game: GameI) => game.platform === platf.title).length}
                                        </Badge>
                                    </NavLink>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Col>
                ) : null}

                {genres && games && platforms ? (
                    <Col xs={12} md={6}>
                        <h3>{t("Genre")}</h3>
                        <Nav className={"mb-3"}>
                            {genres.map((genre: GameGenreI) => (
                                <React.Fragment key={`${genre.name}`}>
                                    {genre.use ? (
                                        <Nav.Item className={"mx-1 my-1"}>
                                            <NavLink
                                                className={`btn btn-sm ${
                                                    genreHasGames(genre.name)
                                                        ? "btn-primary"
                                                        : "btn-outline-primary disabled"
                                                }`}
                                                to={`/games${platform ? "/" + platform : ""}${
                                                    orderCharacter !== genre.name ? "/genre/" + genre.name : ""
                                                }`}
                                            >
                                                {t(genre.name)}{" "}
                                                <Badge bg={"info"}>
                                                    {!platform
                                                        ? [...games].filter(
                                                              (game: GameI) =>
                                                                  game.genre && game.genre.includes(genre.name)
                                                          ).length
                                                        : [...games].filter(
                                                              (game: GameI) =>
                                                                  game.genre &&
                                                                  game.genre.includes(genre.name) &&
                                                                  game.platform ===
                                                                      [...platforms].filter(
                                                                          (p: PlatformI) => p.url === platform
                                                                      )[0].title
                                                          ).length}
                                                </Badge>
                                            </NavLink>
                                        </Nav.Item>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </Nav>
                    </Col>
                ) : null}
            </Row>

            {filteredGames && filteredGames.length > 0 ? (
                <h3 className={"mb-3"}>
                    {t("Showing games")}{" "}
                    {orderBy === "title" ? t("with title starting by") : orderBy === "genre" ? t("from genre") : null}
                    {orderBy ? ' "' : null}
                    {orderBy === "genre" ? t(orderCharacter!) : orderCharacter?.toUpperCase()}
                    {orderBy ? '"' : null}
                    {platform ? (
                        <>
                            {" "}
                            {t("from platform")} {platform.toUpperCase()}
                        </>
                    ) : null}
                    {":"}
                </h3>
            ) : null}

            {orderBy && orderCharacter && filteredGames && filteredGames.length > 0 ? (
                <Row>
                    {filteredGames.map((game: GameI) => {
                        return (
                            <Col key={`game-card-${game.url}`} xl={3} lg={4} sm={6}>
                                <GamePreview game={game} />
                            </Col>
                        );
                    })}
                </Row>
            ) : null}

            {newTwentyGames && !orderBy ? (
                <>
                    <h3>{t("New twenty games")}</h3>
                    <Ranking games={newTwentyGames} />
                </>
            ) : null}

            {topTwentyGames && !orderBy ? (
                <>
                    <h3>{t("Top twenty games")}</h3>
                    <Ranking games={topTwentyGames} />
                </>
            ) : null}

            {worstTwentyGames && !orderBy ? (
                <>
                    <h3>{t("Worst twenty games")}</h3>
                    <Ranking games={worstTwentyGames} />
                </>
            ) : null}
        </>
    );
};
